import { useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { Hook } from '@rossum/api-client/hooks';
import { DetailDrawer, detailDrawerWidths } from '@rossum/ui';
import { ExtendedHook } from '../helpers';
import { GraphDataGroupedPerEvents } from '../hooks/useGraphHooks';
import { UpdateDependenciesForm } from './UpdateDependenciesForm';

export const DRAWER_WIDTH = detailDrawerWidths.small;

type UpdateDependenciesDrawerProps = {
  graphDataGroupedPerEvents: GraphDataGroupedPerEvents;
  onClose: () => void;
  selectedQueueId?: number;
  drawerState: null | {
    selectedEvents: ExtendedHook['events'] | null;
    selectedElement: React.RefObject<HTMLDivElement> | undefined;
    selectedExtension: ExtendedHook | null;
  };
  originalExtensions: Hook[] | undefined;
};

export const UpdateDependenciesDrawer = ({
  drawerState,
  onClose,
  selectedQueueId,
  graphDataGroupedPerEvents,
  originalExtensions,
}: UpdateDependenciesDrawerProps) => {
  const intl = useIntl();

  const scrollToElement = useCallback(
    (el: React.RefObject<HTMLDivElement> | undefined) => {
      if (el?.current) {
        el.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest',
        });
      }
    },
    []
  );

  const handleCloseEscPress = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleCloseEscPress);

    return () => {
      document.removeEventListener('keydown', handleCloseEscPress);
    };
  }, [handleCloseEscPress]);

  const selectedExtension = drawerState?.selectedExtension ?? null;
  const selectedEvents = drawerState?.selectedEvents ?? null;
  const selectedElement = drawerState?.selectedElement ?? undefined;

  const filtredByEvents = graphDataGroupedPerEvents.filter(({ eventNames }) =>
    eventNames.find(event => selectedEvents?.includes(event))
  );

  const graphData = filtredByEvents.flatMap(e => e.graphData);

  const extensionsGraphNodesMap = filtredByEvents.map(
    e => e.extensionGraphNodesMap
  );

  const extensionName = `${selectedExtension?.name}`;

  return (
    <DetailDrawer
      variant="persistent"
      size="small"
      title={extensionName}
      description={intl.formatMessage({
        id: 'containers.queueExtensions.dialog.updateDependencies.title',
      })}
      open={!!selectedExtension}
      onClose={() => onClose()}
      onTransitionEnd={() =>
        !!selectedExtension && scrollToElement(selectedElement)
      }
    >
      {/* We expect this to always exist unless filtredByEvents is calculated incorrectly */}
      {extensionsGraphNodesMap[0] && selectedExtension ? (
        <UpdateDependenciesForm
          key={selectedExtension.id}
          originalExtensions={originalExtensions}
          extensionGraphNodesMap={extensionsGraphNodesMap[0]}
          extensions={graphData.flat()}
          selectedExtension={selectedExtension}
          onClose={onClose}
          selectedQueueId={selectedQueueId}
        />
      ) : null}
    </DetailDrawer>
  );
};
